import React from 'react';
import { css } from '@emotion/react';
import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from '@onward-delivery/design';
import { ChevronLeft, ChevronRight } from '@onward-delivery/design/icons';
import { useScrollOverflow } from './useScrollOverflow';

// Overrides on onward-design's underline tabs (cn/twMerge lets these win). Library defaults:
//   list:    inline-flex items-center gap-6 border-b border-border
//   trigger: text-sm font-medium -mb-px border-b-2 border-transparent px-1 pb-2 pt-1 text-muted
//            data-[state=active]:border-primary data-[state=active]:text-primary
// Trigger `px-*` sets how far the active underline runs past the label; `pb-*` is the gap
// between the label and the underline. The list is at least as wide as the card and grows past it
// (scrolling, with arrows) when the labels don't fit; labels never wrap.
const LIST_CLASSES = 'w-max min-w-full justify-between gap-2';
const TRIGGER_CLASSES = 'whitespace-nowrap px-5 pb-4 pt-1 text-base font-semibold';
// The scrolling strip around the list. Its scrollbar is hidden; the arrows (and trackpad / keyboard)
// scroll it.
const SCROLLER_CLASSES = 'min-w-0 flex-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';
// Arrow buttons. The bottom border continues the tab row's line under them.
const ARROW_CLASSES = 'h-auto shrink-0 self-stretch rounded-none border-b border-border px-1 text-muted';

const ScrollArrow = ({ direction, onClick }) => {
    const Icon = direction < 0 ? ChevronLeft : ChevronRight;
    return (
        <Button
            variant="ghost"
            size="sm"
            onClick={onClick}
            aria-label={direction < 0 ? 'Scroll tabs left' : 'Scroll tabs right'}
            className={ARROW_CLASSES}
        >
            <Icon className="h-5 w-5" />
        </Button>
    );
};

// The card is plain CSS rather than onward-design's Card so it can hold tabs whose content isn't
// design-system (see `scoped` below). It matches Card: white, 1px border, rounded-md, shadow-sm.
// `padding-top` is the space above the tab row.
const cardStyles = css`
    background-color: #ffffff;
    border: 1px solid var(--color-border, #e2e2e2);
    border-radius: 0.5rem;
    box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
    padding: 1.75rem 1.75rem 1.5rem;
`;

const contentStyles = css`
    padding-top: 0.25rem;
`;

/**
 * A card with a scrolling tab row. Set `scoped: false` on a tab holding Material UI content: the
 * `onward-design` scope's reset strips MUI's borders.
 * @param {{ tabs: { value: string, label: string, content: React.ReactNode, scoped?: boolean }[],
 *   label: string, value?: string, onChange?: (value: string) => void }} props opens on the first tab unless `value`
 *   picks one; `label` names the tab list for screen readers
 */
const TabviewCard = ({ tabs, label, value, onChange }) => {
    const { ref, canScrollLeft, canScrollRight, scrollBy } = useScrollOverflow();

    return (
        <div css={cardStyles}>
            <Tabs defaultValue={tabs[0]?.value} value={value} onValueChange={onChange}>
                <div className="onward-design">
                    <div className="flex items-stretch">
                        {canScrollLeft && <ScrollArrow direction={-1} onClick={() => scrollBy(-1)} />}
                        <div ref={ref} className={SCROLLER_CLASSES}>
                            <TabsList variant="underline" aria-label={label} className={LIST_CLASSES}>
                                {tabs.map((tab) => (
                                    <TabsTrigger key={tab.value} value={tab.value} className={TRIGGER_CLASSES}>
                                        {tab.label}
                                    </TabsTrigger>
                                ))}
                            </TabsList>
                        </div>
                        {canScrollRight && <ScrollArrow direction={1} onClick={() => scrollBy(1)} />}
                    </div>
                </div>
                {tabs.map((tab) => (
                    <TabsContent key={tab.value} value={tab.value} css={contentStyles}>
                        {tab.scoped === false ? tab.content : <div className="onward-design">{tab.content}</div>}
                    </TabsContent>
                ))}
            </Tabs>
        </div>
    );
};

export default TabviewCard;
